import { Component, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { DynamicFilterConfig, ModalService, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import _ from 'lodash';
import { EmployeeAccount, EmployeeAccountSearchCommand } from '../../../models/employee-account/employee-account';
import { BusinessRoles } from '../../../models/roles/roles-group';
import { EmployeeAccountProxyService } from '../../../proxies/employee-account.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { AddEmployeeModalComponent } from './_modals/add-employee-modal/add-employee-modal.component';

/** The filter panel's controls; select values arrive as strings ('true' / 'false'). */
interface EmployeeFilterFormModel {
  search: string | null;
  isActive: string | null;
}

type EmployeeFilters = Partial<EmployeeFilterFormModel>;

/**
 * Employees (#5775) — UserMaster employees enabled one by one for trips and employee advances. Only an active
 * employee here can be put on a trip. Adding one picks it from UserMaster; its page sets the accounts it posts to.
 */
@Component({
  selector: 'app-employees',
  templateUrl: './employees.component.html',
})
export class EmployeesComponent {
  private readonly proxy = inject(EmployeeAccountProxyService);
  private readonly modalService = inject(ModalService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly userAuth = inject(UserAuthenticationService);

  private currentSearchCriteria: EmployeeAccountSearchCommand = {};

  /** Viewers see the list only; the API refuses enabling without BusinessAdmin as well. */
  readonly canEdit = this.userAuth.roles.includes(BusinessRoles.BusinessAdmin);

  readonly isLoading = signal(0);
  readonly filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({
    storageKey: 'employee-account-filters',
    config: this.filterConfig,
    defaults: { isActive: 'true' },
  });
  readonly items = signal<EmployeeAccount[]>([]);
  readonly totalRecords = signal(0);
  readonly currentPage = signal(1);
  readonly pageSize = signal(25);
  readonly sortField = signal<string | null>(null);
  readonly sortOrder = signal(1);

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as EmployeeFilters));

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const value = this.translateService.instant(key);
    return value === key ? fallback : value;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'search',
          label: this.t('EmployeeAccount_Filter_Search', 'Code, name or user'),
          placeholder: this.t('EmployeeAccount_Filter_Search_Placeholder', 'Search by code, name or user'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'isActive',
          label: this.t('Status', 'Status'),
          placeholder: this.t('Select_status', 'Select status'),
          options: [
            { label: this.t('EmployeeAccount_Active', 'Active'), value: 'true' },
            { label: this.t('EmployeeAccount_Inactive', 'Inactive'), value: 'false' },
          ],
          showClear: true,
          defaultVisible: true,
        },
      ],
    };
  }

  private searchWithFilters(filters: EmployeeFilters): void {
    const search = (filters.search ?? '').toString().trim();
    const isActive = _.isNil(filters.isActive) || filters.isActive === '' ? null : filters.isActive === 'true';
    this.currentSearchCriteria = { search: search || null, isActive };
    this.currentPage.set(1);
    this.search(this.currentSearchCriteria);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search(this.currentSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = event.field ?? null;
    const newOrder = event.order ?? 1;
    if (newField === this.sortField() && newOrder === this.sortOrder()) return;
    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.search(this.currentSearchCriteria);
  }

  private search(criteria: EmployeeAccountSearchCommand): void {
    this.startLoading();
    this.proxy
      .search(criteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        this.items.set(response.data?.items ?? []);
        this.totalRecords.set(response.data?.totalCount ?? 0);
      });
  }

  redirectToDetails(id: string): void {
    this.router.navigate(['/employee-details'], { queryParams: { id } });
  }

  /** The modal enables the employee itself; an enabled one comes back here to open its page. */
  openAdd(): void {
    this.modalService.open(AddEmployeeModalComponent, { modalClass: 'modal_large' }, {}).subscribe((result) => {
      if (result?.action === 'save' && result.data) {
        this.redirectToDetails(result.data);
      }
    });
  }

  getRow(rowData: unknown): EmployeeAccount {
    return rowData as EmployeeAccount;
  }
}
